import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAdminApi } from "../api/AdminApi";
import { toast } from "react-toastify";
import { useUserApi } from "../api/UserApi";
import { getAssignableRoles } from "../utils/roleUtils";
import { useUser } from "../context/UserContext";
import { handleApiError } from "../utils/handleApiError";
import { useDeleteUserConfirm } from "../context/ConfirmContext ";
const AdminUserProfiles: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { updateUserRoles } = useAdminApi();
  const { getUserByUserId, anonymizeUser } = useUserApi();
  const { user } = useUser();
  const [targetUser, setTargetUser] = useState<any | null>(null);
  const [pendingRoles, setPendingRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const ROLES = getAssignableRoles(user?.roles || []);
  const deleteConfirm = useDeleteUserConfirm();
  const isSuperAdmin = user?.roles?.some((r) => ["SuperAdmin"].includes(r));
  useEffect(() => {
    const loadUser = async () => {
      try {
        const response = await getUserByUserId(Number(id));
        setTargetUser(response.data.data);
        setPendingRoles(response.data.data.roles);
      } catch (err: any) {
        handleApiError(err, "Kunne ikke laste bruker.");
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id]);

  const handleDeleteUser = async () => {
    try {
      const confirmed = await deleteConfirm({
        title: "Slett/Anonymiser Bruker",
        message:
          "Denne handlingen kan ikke angres.\nSkriv SLETT for å bekrefte.",
        confirmText: "Slett",
        cancelText: "Avbryt",
      });

      if (!confirmed) return;

      await anonymizeUser(targetUser?.id); // call your API
    } catch (err: any) {
      const parsed = JSON.parse(err.request.response);
      if (parsed.success === true) {
        toast.success("Bruker anonymisert");
        navigate(-1);
      } else {
        toast.info(parsed.message);
      }
    } finally {
    }
  };

  const toggleRole = (role: string) => {
    setPendingRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  };

  const save = async () => {
    try {
      await updateUserRoles(Number(id), pendingRoles);
      toast.success("Roller oppdatert!");
      navigate("/admin");
    } catch (err: any) {
      handleApiError(err, "Feil ved lagring av roller.");
    }
  };

  if (loading) return <p>Laster...</p>;
  if (!targetUser) return <p>Bruker finnes ikke.</p>;

  return (
    <div className="pt-[90px] p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">
        Administrer {targetUser.username}
      </h1>

      <h2 className="text-lg font-semibold mb-2">Roller</h2>

      <div className="flex flex-col gap-2">
        {ROLES.map((role) => (
          <label
            key={role}
            className="flex items-center gap-3 p-2 bg-orange-50 rounded"
          >
            <input
              type="checkbox"
              checked={pendingRoles.includes(role)}
              onChange={() => toggleRole(role)}
            />
            {role}
          </label>
        ))}
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={save}
          className="bg-orangeBrand text-white px-4 py-2 rounded"
        >
          Lagre
        </button>

        <button
          onClick={() => navigate(-1)}
          className="bg-gray-300 px-4 py-2 rounded"
        >
          Tilbake
        </button>
      </div>
      {isSuperAdmin && (
        <div className="mt-8 flex justify-between items-center">
          <div className="mt-8 p-6 border border-red-300 bg-red-50 rounded-xl">
            <h3 className="text-lg font-semibold text-red-700 mb-2">
              Fareområde
            </h3>
            <p className="text-sm text-red-600 mb-4">
              Her kan du anonymisere brukeren permanent. Dette kan ikke angres.
            </p>

            <button
              onClick={handleDeleteUser}
              className="w-full px-4 py-2 text-white font-semibold bg-red-600 hover:bg-red-700 rounded-lg shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {"Anonymiser Bruker"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserProfiles;
